import dayjs from 'dayjs';

export const format = (n) => {
  return (~~n).toString().padStart(2, '0');
};

export const secondsToTime = (seconds) => {
  return [
    format(seconds / 60 / 60),
    format((seconds / 60) % 60),
    format(seconds % 60),
  ];
};

export const subtractDays = (date, days) => {
  return date.setTime(date.getTime() - days * 24 * 60 * 60 * 1000);
};

export const areDatesSameDay = (date1, date2) => {
  const date1UTC = new Date(
    Date.UTC(date1.getUTCFullYear(), date1.getUTCMonth(), date1.getUTCDate())
  );
  const date2UTC = new Date(
    Date.UTC(date2.getUTCFullYear(), date2.getUTCMonth(), date2.getUTCDate())
  );

  return (
    date1UTC.getFullYear() === date2UTC.getFullYear() &&
    date1UTC.getMonth() === date2UTC.getMonth() &&
    date1UTC.getDate() === date2UTC.getDate()
  );
};

export const getUnixFromSecomDate = (date) => {
  const parseDate =
    date.substr(0, 4) +
    '-' +
    date.substr(4, 2) +
    '-' +
    date.substr(6, 2) +
    'T' +
    date.substr(8, 2) +
    ':' +
    date.substr(10, 2) +
    ':' +
    date.substr(12, 2);
  return dayjs(parseDate).unix();
};

export const getDayjsFromSecomDate = (date) => {
  const parseDate =
    date.substr(0, 4) +
    '-' +
    date.substr(4, 2) +
    '-' +
    date.substr(6, 2) +
    'T' +
    date.substr(8, 2) +
    ':' +
    date.substr(10, 2) +
    ':' +
    date.substr(12, 2);
  return dayjs(parseDate);
};

/**
 * Get default date for the range calendar
 * @returns {Array<String>}
 */
export const getDefaultRangeCalendar = (
  { startDateFormat, endDateFormat } = {
    startDateFormat: 'YYYYMMDDHHmmss',
    endDateFormat: 'YYYYMMDDHHmmss',
  }
) => {
  const range = [
    dayjs().subtract(7, 'days').format(startDateFormat),
    dayjs().format(endDateFormat),
  ];
  return range;
};

/**
 * Get default date for the record range calendar
 * @returns {Array<String>}
 */
export const getDefaultCalendarRecord = () => {
  const range = [
    dayjs().subtract(7, 'days').format('YYYYMMDDHHmmss'),
    dayjs().format('YYYYMMDDHHmmss'),
  ];
  return range;
};

/**
 * Get download time period setting
 * @returns {Array<String>}
 */
export const getDownloadTimePeriod = () => {
  return [
    dayjs()
      .subtract(7, 'days')
      .set('hours', 0)
      .set('minutes', 0)
      .set('seconds', 0)
      .format('YYYYMMDDHHmmss'),
    dayjs().format('YYYYMMDDHHmmss'),
  ];
};

/**
 * Get default date for the record range calendar
 * @returns {Array<String>}
 */
export const getRecordTimelineRange = (startPoint, endPoint) => {
  const startPointJs = dayjs(startPoint);
  const endPointJs = dayjs(endPoint);
  const days = dayjs.duration(endPointJs.diff(startPoint)).asDays();
  const midpoint = dayjs((startPointJs.valueOf() + endPointJs.valueOf()) / 2)
    .set('second', 0)
    .set('hour', 0)
    .set('minute', 0);
  if (days < 6) {
    const start = midpoint.subtract(3, 'days').set('hour', 10);
    const end = midpoint.add(4, 'days').set('hour', 10);

    return { start, end, midpoint };
  }
  if (days < 7) {
    const start = endPointJs.subtract(7, 'days');
    return { start, end: endPointJs, midpoint };
  }
  return { start: startPointJs, end: endPointJs, midpoint };
};

/**
 * Adds time to a date. Modelled after MySQL DATE_ADD function.
 * Example: dateAdd(new Date(), 'minute', 30)  //returns 30 minutes from now.
 * https://stackoverflow.com/a/1214753/18511
 *
 * @param date  Date to start with
 * @param interval  One of: year, quarter, month, week, day, hour, minute, second
 * @param units  Number of units of the given interval to add.
 */
export const dateAdd = (date, interval, units) => {
  if (!(date instanceof Date)) return undefined;
  var ret = new Date(date); //don't change original date
  var checkRollover = function () {
    if (ret.getDate() != date.getDate()) ret.setDate(0);
  };
  switch (String(interval).toLowerCase()) {
    case 'year':
      ret.setFullYear(ret.getFullYear() + units);
      checkRollover();
      break;
    case 'quarter':
      ret.setMonth(ret.getMonth() + 3 * units);
      checkRollover();
      break;
    case 'month':
      ret.setMonth(ret.getMonth() + units);
      checkRollover();
      break;
    case 'week':
      ret.setDate(ret.getDate() + 7 * units);
      break;
    case 'day':
      ret.setDate(ret.getDate() + units);
      break;
    case 'hour':
      ret.setTime(ret.getTime() + units * 3600000);
      break;
    case 'minute':
      ret.setTime(ret.getTime() + units * 60000);
      break;
    case 'second':
      ret.setTime(ret.getTime() + units * 1000);
      break;
    default:
      return undefined;
  }
  return ret;
};

/**
 * Validate date that is today or after
 *
 * @param date
 * 'yyyy-mm-dd'
 */
export const isTodayOrAfter = (date) => {
  const targetDate = date;
  const currentDate = dayjs();
  return (
    !targetDate ||
    dayjs(targetDate).isAfter(currentDate, 'day') ||
    dayjs(targetDate).isSame(currentDate, 'day')
  );
};
